import { Selector } from '@ngxs/store';
import { MobilePaymentType } from '../../enums/mobile-payment-type';
import { MobilePhoneProduct } from '../../models/mobilephone-product';
import {
  CustomerProductMobilePhoneState,
  CustomerProductsMobilePhoneStateModel,
} from './customer-product-mobilephone.state';
import { CustomerProductSelectors } from '../customer-product/customer-product.selectors';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { CustomerEquipmentUpgradeAgreement } from '@sales/modules/mobilephone/models/equipment-upgrade-agreement.model';
import { MobilePhoneBundle } from '@sales/models/mobile-phone-bundle.model';
import { MobilBytteSelectors } from '@app/sales/modules/mobilephone/states/mobilbytte/mobilbytte.selectors';
import { HardwareVariant } from '@app/sales/modules/mobilephone/models/mobilephone-variant';

export class CustomerProductMobilePhoneSelectors {
  @Selector([CustomerProductMobilePhoneState])
  public static selectedMobilePhoneId(state: CustomerProductsMobilePhoneStateModel): string {
    return state.selectedMobilephoneId;
  }

  @Selector([CustomerProductMobilePhoneState])
  public static selectedMobileAgreementId(state: CustomerProductsMobilePhoneStateModel): string {
    return state.selectedMobileAgreementId;
  }

  @Selector([CustomerProductMobilePhoneState])
  public static mobilePhoneProducts(state: CustomerProductsMobilePhoneStateModel): MobilePhoneProduct[] {
    return state.products;
  }

  @Selector([CustomerProductMobilePhoneState])
  public static getNewMobilePhoneProducts(state: CustomerProductsMobilePhoneStateModel): MobilePhoneProduct[] {
    return state.products.filter(p => !p.isExistingSubscription);
  }

  @Selector([
    CustomerProductSelectors.bundledVoiceProducts,
    CustomerProductMobilePhoneSelectors.mobilePhoneProducts,
    MobilBytteSelectors.existingEquipmentUpgradeAgreements,
  ])
  static getMobilePhoneBundles(
    connectedVoiceProducts: VoiceProduct[],
    newMobilePhoneProducts: MobilePhoneProduct[],
    existingEquipmentUpgradeAgreements: CustomerEquipmentUpgradeAgreement[]
  ): MobilePhoneBundle[] {
    return newMobilePhoneProducts
      .filter(mobileProduct => Boolean(mobileProduct.voiceProductId))
      .map(mobileProduct => {
        return {
          connectedVoiceProduct: connectedVoiceProducts.find(x => x.id === mobileProduct.voiceProductId),
          connectedMobileProduct: mobileProduct,
          connectedMobileAgreement: existingEquipmentUpgradeAgreements.find(
            x => x.agreementId === mobileProduct.existingAgreementId
          ),
        };
      })
      .filter(bundle => Boolean(bundle.connectedVoiceProduct));
  }

  @Selector([CustomerProductMobilePhoneState])
  static hasMobilBytteProductInCart(state: CustomerProductsMobilePhoneStateModel): boolean {
    return state.products
      .filter(p => !p.isExistingSubscription)
      .some((product: MobilePhoneProduct) => product.paymentType === MobilePaymentType.EquipmentUpgrade);
  }

  @Selector([CustomerProductMobilePhoneState])
  static getMobilBytteProductInCart(state: CustomerProductsMobilePhoneStateModel): MobilePhoneProduct | null {
    const mobileBytteProduct = state.products.find(product => product.paymentType === MobilePaymentType.EquipmentUpgrade);
    return mobileBytteProduct || null;
  }

  @Selector([CustomerProductMobilePhoneState])
  static hasSendInPost(state: CustomerProductsMobilePhoneStateModel): boolean {
    return state.products.some((product: MobilePhoneProduct) => product.sendInPost);
  }

  @Selector([CustomerProductMobilePhoneState, CustomerProductSelectors.getAllVoiceProducts])
  static mobileProductsHaveError(state: CustomerProductsMobilePhoneStateModel, allVoiceProducts: VoiceProduct[]): boolean {
    return allVoiceProducts.length === 0 && state.products.some(p => p.insuranceOption);
  }

  @Selector([CustomerProductMobilePhoneState])
  static getMobilePhoneGtins(state: CustomerProductsMobilePhoneStateModel): string[] {
    return state.products.map(p => p.gtin);
  }

  @Selector([CustomerProductMobilePhoneState])
  static isValidatingImei(state: CustomerProductsMobilePhoneStateModel): boolean {
    return state.isValidatingImei;
  }

  @Selector([CustomerProductMobilePhoneState, CustomerProductSelectors.selectedSubscription])
  static getMobilePhoneVariantInSelectedSubscriptionBundle(
    state: CustomerProductsMobilePhoneStateModel,
    selectedSubscription: VoiceProduct
  ): HardwareVariant {
    return state.products.find(x => x.voiceProductId === selectedSubscription.id)?.mobilePhoneVariant;
  }
}
